// Staff console is internal Rezeta-staff tooling (not the patient-facing
// product), so its copy is English per the feature spec §7.
export const staffStrings = {
  consoleTitle: 'Rezeta Staff',
  navInstitutions: 'Institutions',
  navSecurity: 'Security',
  navPlatformUsers: 'Platform users',
  navSso: 'SSO connections',
  pageTitle: 'New institution',
  pageSubtitle: 'Create a new institution and its initial super admin.',
  fieldInstitutionName: 'Institution name',
  fieldType: 'Type',
  fieldPlan: 'Plan',
  fieldAdminName: 'Admin name',
  fieldAdminEmail: 'Admin email',
  typeOptions: {
    solo: 'Solo',
    practice: 'Practice',
    clinic: 'Clinic',
    enterprise: 'Enterprise',
  },
  planOptions: {
    free: 'Free',
    solo: 'Solo',
    practice: 'Practice',
    clinic: 'Clinic',
  },
  submit: 'Create institution',
  submitting: 'Creating…',
  error: 'Could not create the institution. Check the details and try again.',
  successTitle: 'Institution created',
  successBody: (email: string): string => `A set-password email was sent to ${email}.`,
  successBackToList: 'Back to institutions',
  noAccessTitle: 'No staff access',
  noAccessBody:
    'You are signed in, but this account is not provisioned for the staff console. Ask a platform admin to add it, then sign in again.',
  noAccessSignOut: 'Sign out',
} as const

export const institutionsStrings = {
  pageTitle: 'Institutions',
  pageSubtitle: 'All institutions on the platform.',
  newButton: 'New institution',
  tableInstitution: 'Institution',
  tablePlan: 'Plan',
  tableUsers: 'Users',
  tableCreated: 'Created',
  usersCell: (active: number, total: number): string => `${active} of ${total}`,
  unnamed: 'Unnamed institution',
  loadError: 'Could not load institutions.',
  emptyTitle: 'No institutions yet',
  emptyBody: 'Create the first institution to get started.',
} as const

export const platformUsersStrings = {
  pageTitle: 'Platform users',
  pageSubtitle: 'Internal Rezeta staff accounts with access to this console.',
  newUserButton: 'New user',
  tableUser: 'User',
  tableStatus: 'Status',
  tableLastAccess: 'Last access',
  statusActive: 'Active',
  statusInvited: 'Invite pending',
  statusDeactivated: 'Deactivated',
  youChip: 'You',
  resendButton: 'Resend link',
  deactivateButton: 'Deactivate',
  reactivateButton: 'Reactivate',
  neverAccessed: '—',
  formTitle: 'Create platform user',
  nameLabel: 'Full name',
  namePlaceholder: 'Laura Medina',
  emailLabel: 'Email',
  emailPlaceholder: 'laura@rezeta.do',
  linkNote: 'No password is set here — the person receives a set-password link by email.',
  cancelButton: 'Cancel',
  createButton: 'Create user',
  creatingButton: 'Creating…',
  createError: 'Could not create the user. Check the email and try again.',
  actionError: 'The action failed. Try again.',
  loadError: 'Could not load platform users.',
  emptyTitle: 'No platform users yet',
  emptyBody: 'Create the first staff account to get started.',
} as const

export const staffSecurityStrings = {
  pageTitle: 'Security',
  pageSubtitle: 'Cross-institution login activity and account health.',
  tileActiveInstitutions: 'Active institutions',
  tileActiveUsers: 'Active users · 30d',
  tileLogins7d: 'Logins · 7d',
  tileDormant: 'Dormant accounts · 60d',
  tileMfaAdoption: 'MFA adoption',
  dormantCallout: (count: number): string => `${count} accounts with no access in 60 days`,
  tableInstitution: 'Institution',
  tablePlan: 'Plan',
  tableMau: 'MAU',
  tableLogins: 'Logins · 14d',
  tableSignals: 'Signals',
  unnamed: 'Unnamed institution',
  signalDormant: (count: number): string => `${count} dormant`,
  signalPending: (count: number): string => `${count} pending invites`,
  loadError: 'Could not load the security overview.',
  emptyTitle: 'No institutions yet',
  emptyBody: 'Institution activity will appear here once accounts start signing in.',
} as const

export const ssoStrings = {
  pageTitle: 'SSO connections',
  pageSubtitle: 'Single sign-on connections per institution.',
  newButton: 'New connection',
  tableInstitution: 'Institution',
  unnamedInstitution: 'Unnamed institution',
  tableName: 'Name',
  tableDomains: 'Domains',
  tableStatus: 'Status',
  tablePassword: 'Password',
  passwordAllowed: 'Yes',
  passwordBlocked: 'No',
  statusActive: 'Active',
  statusDisabled: 'Deactivated',
  loadError: 'Could not load SSO connections.',
  emptyTitle: 'No SSO connections yet',
  emptyBody: 'Create the first connection to enable single sign-on.',
  testButton: 'Test',
  testingButton: 'Testing…',
  testSuccessTitle: 'Connection verified',
  testSuccessChecks: (checks: string[]): string => `Verified: ${checks.join(', ')}`,
  testFailureTitle: 'The test failed',
  editButton: 'Edit',
  deleteButton: 'Delete',
  deactivateButton: 'Deactivate',
  activateButton: 'Activate',
  createFormTitle: 'New SSO connection',
  editFormTitle: 'Edit SSO connection',
  fieldTenant: 'Institution',
  fieldTenantPlaceholder: 'Select an institution',
  fieldDisplayName: 'Display name',
  fieldDisplayNamePlaceholder: 'e.g. Azure AD',
  fieldIssuerUrl: 'Issuer URL',
  fieldIssuerUrlPlaceholder: 'https://login.microsoftonline.com/…',
  fieldClientId: 'Client ID',
  fieldClientIdPlaceholder: 'client_id',
  fieldClientSecret: 'Client secret',
  fieldClientSecretPlaceholder: '••••••••',
  fieldClientSecretEditHelper: 'Leave empty to keep the current secret',
  fieldDomains: 'Domains',
  fieldDomainsPlaceholder: 'clinica.do, hospital.do',
  fieldDomainsHelper: 'Separate multiple domains with commas.',
  fieldAllowPassword: 'Allow password sign-in',
  cancelButton: 'Cancel',
  confirmButton: 'Confirm',
  processingLabel: 'Processing...',
  createButton: 'Create connection',
  creatingButton: 'Creating…',
  saveButton: 'Save',
  savingButton: 'Saving…',
  createError: 'Could not create the connection. Check the details and try again.',
  saveError: 'Could not save the connection. Check the details and try again.',
  actionError: 'The action failed. Try again.',
  confirmDeactivateTitle: 'Deactivate connection',
  confirmDeactivateBody: (name: string): string =>
    `Deactivate the SSO connection "${name}"? Users on its domains will no longer be able to sign in with SSO.`,
  confirmActivateTitle: 'Activate connection',
  confirmActivateBody: (name: string): string => `Activate the SSO connection "${name}"?`,
  confirmDeleteTitle: 'Delete connection',
  confirmDeleteBody: (name: string): string =>
    `Delete the SSO connection "${name}"? This action cannot be undone.`,
} as const
